import { TranslateUiPipe } from '@core/translation/translate-ui-pipe.pipe';
import { Directive, Input, HostBinding, OnInit, Optional } from '@angular/core';
import { UntypedFormControl, ControlContainer } from '@angular/forms';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';

const defaultErrorMessages = new Map<string, string>([
  ['required', marker('Field is required')],
  ['email', marker('Please provide a valid email')]
]);

@Directive({
  selector: '[appFormControlError]',
  providers: [TranslateUiPipe]
})
export class FormControlErrorDirective implements OnInit {
  @Input('appFormControlError') controlName: string;
  @Input() formErrorControl: UntypedFormControl;
  @Input() errorMessageText: string;
  @Input() multiErrors: boolean = false;

  @HostBinding('class.is-error')
  get isError(): boolean {
    const control = this.control;
    return !!control && control.enabled && !control.valid && (this.multiErrors || control.touched);
  }

  @HostBinding('attr.data-error')
  get errorText(): string {
    const control = this.control;
    if (!control || !control.errors) {
      return '';
    }
    if (this.errorMessageText) {
      return this.translate.transform(this.errorMessageText);
    }

    const result = Object.keys(control.errors)
      .map((errorKey) => {
        const error = control.errors[errorKey];

        switch (true) {
          case !!error.min: {
            return `${this.translate.transform(marker('Min value is'))} ${error.min}`;
          }
          case !!error.max: {
            return `${this.translate.transform(marker('Max value is'))} ${error.max}`;
          }
          case !!error.requiredLength && errorKey === 'minlength': {
            return `${this.translate.transform(marker('Min'))} ${error.requiredLength} ${this.translate.transform(marker('characters'))}`;
          }
          case !!error.requiredLength && errorKey === 'maxlength': {
            return `${this.translate.transform(marker('Max'))} ${error.requiredLength} ${this.translate.transform(marker('characters'))}`;
          }
          default: {
            return error.message ? error.message : defaultErrorMessages.get(errorKey);
          }
        }
      })
      .join('. ');
    return this.translate.transform(result);
  }

  get control(): UntypedFormControl {
    if (this.formErrorControl) {
      return this.formErrorControl;
    }
    return this.parent.control.get(this.controlName) as UntypedFormControl;
  }
  constructor(
    private translate: TranslateUiPipe,
    @Optional() private parent?: ControlContainer
  ) {}

  ngOnInit() {
    if (!this.parent && !this.formErrorControl) {
      throw new Error(
        'FormControlErrorDirective should be either used inside of a ControlContainer (e.g. FormGroup) or have formErrorControl specified'
      );
    }
  }
}
